import { useHead, useRoute } from '#imports'
import type { Ref } from 'vue'

import type { BasePageContent } from '@backmarket/http-api/src/api-specs-content/models/base-page-content'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { omit } from '@backmarket/utils/object/omit'
import type { Maybe } from '@backmarket/utils/types/Maybe'

import { useAddCanonicalLink } from '~/scopes/seo/composables/useAddCanonicalLink'

import translations from '../Landing.translations'
import { CMS } from '../routes-names'

import { useCMSAlternateLinks } from './useCMSAlternateLinks'
import { useCMSMeta } from './useCMSMeta'

export type CMSPageContentRef = Ref<Maybe<BasePageContent>>

export function buildHead<T extends BasePageContent>(data: Ref<Maybe<T>>) {
  if (!data.value)
    return {
      title: '',
      titleTemplate: '',
      meta: [],
      link: [],
    }

  const i18n = useI18n()
  const companyName = i18n(translations.companyName)
  const meta = useCMSMeta(data)
  const alternateLinks = useCMSAlternateLinks(data)

  return {
    title: data.value.titleSeo,
    titleTemplate: `%s | ${companyName}`,
    meta,
    link: [...alternateLinks],
  }
}

export function useCMSHead<T extends BasePageContent>(
  data: Ref<T | null | undefined>,
) {
  if (!data.value) return

  const route = useRoute()

  const isLegalPage =
    route.name === CMS.LEGAL_PAGE || route.name === CMS.LEGAL_PREVIOUS_VERSION

  const head = buildHead(data)
  if (isLegalPage) {
    /**
     * Legal pages do not serve any SEO purpose. Also, some legal pages only exist in a subset of countries.
     * By avoiding the alternate links of these pages, we prevent crawlers from fetching inexistent pages.
     */
    useHead(omit(head, ['link']))
  } else {
    useHead(buildHead(data))
    useAddCanonicalLink()
  }
}
